import React, { ForwardedRef, forwardRef, useEffect, useMemo, useState } from 'react';
import { ActionsContainer, HeaderContainer, HeaderFixedContainer, ActionsLinkContainer } from './Header.styles';
import MenuLink from 'src/elements/links/MenuLink';
import { PageProps } from 'gatsby';
import { LinkProps } from 'src/elements/links/2023_Q4/LinkBranded';
import { HeaderDesignControllerContextData, useHeaderDesignController } from 'src/hooks/useHeaderDesignController';
import HamburgerButton from './HamburgerButton/HamburgerButton';
import SideBar from './SideBar/SideBar';
import TopInfoBar from 'organisms/Shared/Layout/TopInfoBar/TopInfoBar';
import SignInButton from 'src/elements/links/2023_Q4/SignInButton';
import { createHeaderLinks } from './Header.links';
import MightyHeaderLogo, { MightyHeaderLogoType } from 'src/elements/header/MightyLogoLink';
import HeaderCta from 'src/elements/header/HeaderCta';
import { useExpandedMenuController } from 'src/hooks/useExpandedMenuController';

const DEFAULT_HEIGHT_TRANSITION_TIME_IN_MILLISECONDS = 500;
// Wait for logo initial animation to run + 1400ms for each letter delay and smooth transition

const UNPINNED_SCROLL_Y_POSITION = 80;

interface HeaderProps {
  locationProps: PageProps['location'];
  heightTransitionTime?: number;
  elementsAppearTime?: number;
  defaultBackgroundTheme?: HeaderDesignControllerContextData['backgroundTheme'];
  defaultTextColorTheme?: HeaderDesignControllerContextData['textColorTheme'];
  unpinnedBackgroundTheme?: HeaderDesignControllerContextData['backgroundTheme'];
  unpinnedTextColorTheme?: HeaderDesignControllerContextData['textColorTheme'];
  callToActionColorScheme?: LinkProps['colorScheme'];
  linksCollection?: 'standard' | 'pro';
  logo?: MightyHeaderLogoType;
  callToAction?: 'standard' | 'pro';
  menuActions?: () => JSX.Element;
  callToActionUrl?: string;
  hideInternalLinks?: boolean;
  hideSignInButton?: boolean;
  hideCallToAction?: boolean;
  hideHamburgerButton?: boolean;
  showHamburgerButtonOnDesktop?: boolean;
  isLogoClickable?: boolean;
  forceScale?: boolean;
  callToActionText?: string;
  // An additional Statsig Event when CTA is clicked, independent of "cta_clicked"
  callToActionStatsigEvent?: string;
  sideBarCallToAction?: {
    callToActionColorScheme?: LinkProps['colorScheme'];
    callToActionColorSchemeVariant?: LinkProps['colorSchemeVariant'];
  };
}

const Header = (
  {
    locationProps,
    menuActions,
    linksCollection = 'standard',
    logo = 'standard',
    callToAction = 'standard',
    callToActionUrl,
    defaultBackgroundTheme = 'transparent',
    defaultTextColorTheme = 'light',
    unpinnedBackgroundTheme = 'light',
    unpinnedTextColorTheme = 'dark',
    heightTransitionTime = DEFAULT_HEIGHT_TRANSITION_TIME_IN_MILLISECONDS,
    callToActionColorScheme = 'Sunshine',
    hideCallToAction = false,
    hideInternalLinks = false,
    hideSignInButton = false,
    showHamburgerButtonOnDesktop = false,
    hideHamburgerButton = false,
    isLogoClickable = true,
    forceScale = false,
    callToActionText,
    callToActionStatsigEvent = null,
    sideBarCallToAction = {
      callToActionColorScheme: 'Sunshine',
      callToActionColorSchemeVariant: 'primary',
    },
  }: HeaderProps,
  ref: ForwardedRef<HTMLDivElement>
): JSX.Element => {
  const [isPined, setIsPined] = useState(true);
  const [isSideBarOpen, setIsSideBarOpen] = useState(false);
  const { isExpanded: isExpandedMenuOpen } = useExpandedMenuController();

  const { backgroundTheme, textColorTheme, setBackgroundTheme, setTextColorTheme } = useHeaderDesignController();

  const defaultCallToActionUrl =
    callToAction === 'pro' ? '/request-a-call-form' : 'https://cohost.mn.co/?plan=free-trial';
  const callToActionLink = callToActionUrl ?? defaultCallToActionUrl;

  useEffect(() => {
    // Reset header design controller state
    setBackgroundTheme(null);
    setTextColorTheme(null);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleScroll = () => {
      setIsPined(window.scrollY < UNPINNED_SCROLL_Y_POSITION);
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const handleHamburgerClick = () => {
    setIsSideBarOpen((prev) => !prev);
  };

  const backgroundThemeToUse = (() => {
    if (isSideBarOpen) return 'light';
    if (isExpandedMenuOpen) return 'light';
    if (backgroundTheme) return backgroundTheme;
    if (!isPined && unpinnedBackgroundTheme) return unpinnedBackgroundTheme;

    return defaultBackgroundTheme ?? 'transparent';
  })();

  const textColorThemeToUse = (() => {
    if (isSideBarOpen) return 'dark';
    if (isExpandedMenuOpen) return 'dark';
    if (textColorTheme) return textColorTheme;
    if (!isPined && unpinnedTextColorTheme) return unpinnedTextColorTheme;

    return defaultTextColorTheme ?? 'light';
  })();

  const { topBarLinks, sideBarLinks } = useMemo(() => {
    const topBarLinks = createHeaderLinks(linksCollection, 'top_menu');
    const sideBarLinks = createHeaderLinks(linksCollection, 'side_menu');

    return { topBarLinks, sideBarLinks };
  }, [linksCollection]);

  const logoLinkUrl = (() => {
    // If we are on the Pro homepage, it should link to the homepage.
    // Any other Pro branded page should lead to the Pro homepage.
    if (locationProps?.pathname === '/pro') return '/';

    return logo === 'pro' ? '/pro' : '/';
  })();

  const renderHeader = () => (
    <HeaderFixedContainer>
      <TopInfoBar />
      <HeaderContainer
        id='header'
        backgroundTheme={backgroundThemeToUse}
        isPined={isPined}
        scaled={forceScale}
        ref={ref}
      >
        <MightyHeaderLogo
          logoType={logo}
          heightTransitionTime={heightTransitionTime}
          textColorTheme={textColorThemeToUse}
          isClickable={isLogoClickable}
          className={`${forceScale ? 'force-scale' : ''}`}
          to={logoLinkUrl}
        />
        <ActionsContainer
          textColorTheme={textColorThemeToUse}
          showHamburgerButtonOnDesktop={showHamburgerButtonOnDesktop}
          className={`${forceScale ? 'force-scale' : ''}`}
        >
          <ActionsLinkContainer>
            {!hideInternalLinks &&
              topBarLinks?.map((link, index) => (
                <MenuLink
                  key={index}
                  locationPathname={locationProps?.pathname}
                  fadeIn={linksCollection !== 'pro'}
                  {...link}
                />
              ))}
          </ActionsLinkContainer>
          {!hideSignInButton && <SignInButton parentBackgroundTheme={backgroundThemeToUse} />}
          {!hideCallToAction && (
            <HeaderCta
              callToAction={callToAction}
              callToActionLink={callToActionLink}
              callToActionColorScheme={callToActionColorScheme}
              callToActionText={callToActionText}
              callToActionStatsigEvent={callToActionStatsigEvent}
            />
          )}
          {!hideHamburgerButton && (
            <HamburgerButton
              handleClick={handleHamburgerClick}
              isActive={isSideBarOpen}
            />
          )}
          {menuActions && menuActions()}
        </ActionsContainer>
      </HeaderContainer>
    </HeaderFixedContainer>
  );

  return (
    <>
      {renderHeader()}
      <SideBar
        callToActionColorScheme={sideBarCallToAction.callToActionColorScheme}
        callToActionColorSchemeVariant={'primary'}
        isOpen={isSideBarOpen}
        openStyle={'full-width'}
        internalLinks={sideBarLinks}
        callToAction={callToAction}
        callToActionText={callToActionText}
        callToActionStatsigEvent={callToActionStatsigEvent}
        backgroundTheme={backgroundThemeToUse}
        textColorTheme={textColorThemeToUse}
      />
    </>
  );
};

export default forwardRef(Header);
